import {
    faArrowDownBigSmall,
    faArrowUpBigSmall,
    faSortUpDown,
} from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
    flexRender,
    type Header,
    type HeaderGroup,
    type Table,
} from "@tanstack/react-table";
import { useAtom } from "jotai";
import { useCallback } from "react";
import { CustomCheckbox } from "../../ui/CustomCheckbox";
import { tableStateFamily } from "./state";
import { BaseTextSearchFilter } from "./TableFilters";

export interface CustomFilterWidgetProps {
    isFiltered: boolean;
    // TODO: Improve typing for custom filters here? (Maybe not?)
    // Need to ensure the filterValue is valid for the apiClient
    // otherwise it will be ignored.
    filterValue?: any;
    setFilterValue: (newFilterValue?: any) => void;
    extraFilterProps?: { [key: string]: any };
}

interface TableFilterSortProps {
    dataName: string;
    header: Header<any, any>;
}

export const TableFilter = (props: TableFilterSortProps) => {
    // Extract columnDef and filter definition parameters
    const columnDef = props.header.column.columnDef;
    const filterKey = columnDef.meta?.filteringKey || columnDef.id;

    // Retrieve table state and filter values
    const [tableState, setTableState] = useAtom(
        tableStateFamily({ tableId: props.dataName }),
    );
    const value = tableState.filters[filterKey];
    const setValue = useCallback(
        (newValue: any) => {
            setTableState((ts) => {
                return {
                    ...ts,
                    filters: {
                        ...ts.filters,
                        [filterKey]: newValue,
                    },
                    // Changing a filter returns to the first page.
                    pagination: { ...ts.pagination, pageIndex: 0 },
                };
            });
        },
        [filterKey, setTableState],
    );

    if (columnDef.meta && columnDef.meta.customFilterWidget) {
        return (
            <columnDef.meta.customFilterWidget
                isFiltered={!!value}
                filterValue={value}
                setFilterValue={setValue}
                extraFilterProps={columnDef.meta.extraFilterProps}
            />
        );
    }

    return (
        <BaseTextSearchFilter
            isFiltered={!!value}
            filterValue={value as string}
            setFilterValue={setValue}
        />
    );
};

export const TableSort = (props: TableFilterSortProps) => {
    const column = props.header.column;
    const sorted = column.getIsSorted();
    const sortIndex = column.getSortIndex();

    return (
        <button
            type="button"
            className="relative inline-flex items-center px-1"
            onClick={column.getToggleSortingHandler()}
        >
            <FontAwesomeIcon
                icon={
                    !sorted
                        ? faSortUpDown
                        : sorted === "asc"
                          ? faArrowUpBigSmall
                          : faArrowDownBigSmall
                }
                className={`w-3 ${sorted ? "text-ae-blue-900" : "text-gray-400"}`}
            />
            {/* priority badge, bottom-left corner, only in multi-sort */}
            {sortIndex >= 0 && column.getCanMultiSort() && (
                <span className="absolute -bottom-[3px] -left-[1px] text-[8px] leading-none tabular-nums pointer-events-none">
                    {sortIndex + 1}
                </span>
            )}
        </button>
    );
};

interface TableHeaderProps<T> {
    dataName: string;
    headers: HeaderGroup<T>[];
    table: Table<T>;
}

export const TableHeader = <T,>(props: TableHeaderProps<T>) => (
    <thead className="bg-ae-gray-100 text-left text-sm font-bold text-ae-blue-900 border-b">
        {props.headers.map((headerGroup) => (
            <>
                <tr key={headerGroup.id}>
                    {props.table.options.enableRowSelection && (
                        <th className="px-4 py-3 hover:bg-neutral-200 border-r border-gray-200 last:border-r-0">
                            <CustomCheckbox
                                checked={
                                    props.table?.getIsAllRowsSelected()
                                        ? "yes"
                                        : props.table?.getIsSomeRowsSelected()
                                          ? "partial"
                                          : "no"
                                }
                                onClick={() => {
                                    if (props.table?.getIsAllRowsSelected()) {
                                        props.table.setRowSelection(() => ({}));
                                    } else {
                                        props.table.toggleAllRowsSelected(true);
                                    }
                                }}
                            />
                        </th>
                    )}
                    {headerGroup.headers.map((header) => (
                        <th
                            key={header.id}
                            className="px-4 py-3 hover:bg-neutral-200 border-r border-gray-200 last:border-r-0"
                            style={{
                                width:
                                    header.getSize() === Number.MAX_SAFE_INTEGER
                                        ? "auto"
                                        : header.getSize(),
                            }}
                        >
                            {header.isPlaceholder ? null : (
                                <span className="flex items-center justify-between whitespace-nowrap">
                                    {flexRender(
                                        header.column.columnDef.header,
                                        header.getContext(),
                                    )}
                                    <div className="ml-3 flex items-center gap-1">
                                        {header.column.getCanSort() && (
                                            <TableSort
                                                dataName={props.dataName}
                                                header={header}
                                            />
                                        )}
                                        {header.column.getCanFilter() && (
                                            <TableFilter
                                                dataName={props.dataName}
                                                header={header}
                                            />
                                        )}
                                    </div>
                                </span>
                            )}
                        </th>
                    ))}
                </tr>
            </>
        ))}
    </thead>
);
